import React, { useState, useEffect } from 'react';
import { Save, FileText, Receipt, CreditCard, Info } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import axios from 'axios';

const RECEIPT_TYPES = [
  { key: 'booking', label: 'Booking Fee Receipt', icon: FileText, color: 'bg-amber-100 text-amber-700', description: 'Shown at footer of every seat booking receipt' },
  { key: 'admission', label: 'Admission Receipt', icon: Receipt, color: 'bg-emerald-100 text-emerald-700', description: 'Shown at footer of admission fee receipt' },
  { key: 'installment', label: 'Installment Fee Receipt', icon: CreditCard, color: 'bg-indigo-100 text-indigo-700', description: 'Shown at footer of every installment / fee-payment receipt' },
];

const RTEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep content in sync when external value changes (eg. after initial fetch)
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) return <div className="min-h-[140px] border rounded-md p-3 text-gray-400">Loading editor...</div>;

  const btn = (active, onClick, label) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 text-xs rounded ${active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50">
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'Bold')}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'Italic')}
        {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), '• List')}
        {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), '1. List')}
        {btn(editor.isActive('heading', { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'H3')}
        {btn(false, () => editor.chain().focus().undo().run(), 'Undo')}
        {btn(false, () => editor.chain().focus().redo().run(), 'Redo')}
      </div>
      <EditorContent editor={editor} className="prose prose-sm max-w-none p-3 min-h-[140px] focus:outline-none [&_*]:outline-none" />
    </div>
  );
};

const ReceiptTerms = () => {
  const [data, setData] = useState({
    booking: { terms_html: '' },
    admission: { terms_html: '' },
    installment: { terms_html: '' },
  });
  const [savingType, setSavingType] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/master-settings/receipt-terms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData({
        booking: res.data.booking || { terms_html: '' },
        admission: res.data.admission || { terms_html: '' },
        installment: res.data.installment || { terms_html: '' },
      });
    } catch (e) {
      console.error('Error loading receipt terms', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (type) => {
    setSavingType(type);
    try {
      await axios.put(
        `${API_URL}/api/master-settings/receipt-terms/${type}`,
        { terms_html: data[type].terms_html, is_active: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`${RECEIPT_TYPES.find(t => t.key === type).label} terms saved!`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingType(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Receipt Terms &amp; Conditions</h1>
        <p className="text-sm text-gray-500 mt-1">
          Customize the Terms &amp; Conditions printed at the bottom of each receipt type for your institution.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold">How it works:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Each section below controls a specific receipt type.</li>
            <li>Saved terms will automatically appear at the bottom of generated receipts.</li>
            <li>Leave blank to use the default footer with no extra terms.</li>
            <li>Use bullet lists (• List) for cleaner formatting on printouts.</li>
          </ul>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg p-10 text-center text-gray-500">Loading...</div>
      ) : (
        RECEIPT_TYPES.map(({ key, label, icon: Icon, color, description }) => (
          <div key={key} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{label}</h3>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
              </div>
              <button
                onClick={() => handleSave(key)}
                disabled={savingType === key}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                data-testid={`save-${key}-terms-btn`}
              >
                <Save className="h-4 w-4" />
                {savingType === key ? 'Saving...' : 'Save'}
              </button>
            </div>
            <div className="p-4">
              <RTEditor
                value={data[key].terms_html}
                onChange={(html) => setData(prev => ({ ...prev, [key]: { ...prev[key], terms_html: html } }))}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ReceiptTerms;
